import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Award, Users, Globe, Shield } from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      icon: Award,
      title: "Quality Excellence",
      description: "We source only the finest fabrics, ensuring every meter meets our rigorous quality standards."
    },
    {
      icon: Users,
      title: "Customer First",
      description: "Our relationships with clients are built on trust, transparency, and dedicated service."
    },
    {
      icon: Globe,
      title: "Global Sourcing",
      description: "We import premium fabrics from trusted suppliers worldwide to bring you the best selection."
    },
    {
      icon: Shield,
      title: "Reliability",
      description: "Consistent quality and timely delivery - we stand behind every order we fulfill."
    }
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      <Helmet>
        <title>About DUGARS | Trusted Fabric Traders</title>
        <meta name="description" content="Learn about DUGARS — a trusted name in fabric trading specializing in premium imported nylon rayon and innovative structural knits." />
        <link rel="canonical" href="https://thedugars.in/about" />
        <meta property="og:title" content="About DUGARS | Trusted Fabric Traders" />
        <meta property="og:description" content="A trusted name in fabric trading specializing in premium imported nylon rayon." />
        <meta property="og:url" content="https://thedugars.in/about" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="About DUGARS | Trusted Fabric Traders" />
        <meta name="twitter:description" content="A trusted name in fabric trading specializing in premium imported nylon rayon." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
            {/* Image */}
            <div className="md:col-span-6 order-2 md:order-1">
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden image-hover-zoom">
                  <img
                    src="/api/images/about_section.png"
                    alt="DUGARS fabric warehouse"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative element */}
                <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-[#4A5D4E]/10 -z-10" />
              </div>
            </div>

            {/* Content */}
            <div className="md:col-span-6 order-1 md:order-2">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-6 block">
                About DUGARS
              </span>
              <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#1C1C1A] leading-tight mb-8">
                A Legacy of<br />
                <span className="italic">Quality Fabrics</span>
              </h1>
              <p className="text-[#6B6A65] font-light leading-relaxed text-lg mb-8">
                DUGARS has been a trusted name in the fabric trading industry, 
                specializing in Pique, Herringbone, and Dobby weave fabrics — 
                including 50s SLUB Pique, 50s NR Pique, 60s NR Herringbone, Boss Dobby, 
                and 50s NR Plain Dyed collections. Our commitment to quality and customer 
                satisfaction has made us a preferred partner for manufacturers across India.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-6 block">
              Our Story
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#1C1C1A] mb-8">
              Building Trust Through Quality
            </h2>
            <div className="text-[#6B6A65] font-light leading-relaxed space-y-6">
              <p>
                DUGARS was founded with a simple mission: to provide the textile industry 
                with access to the world&apos;s finest fabrics. What started as a small trading 
                operation has grown into a respected name in the fabric wholesale business.
              </p>
              <p>
                We specialize in premium knit and woven fabrics — including Pique, 
                Herringbone, and Dobby constructions — known for their exceptional hand feel, 
                vibrant colours, and durability for modern garment applications.
              </p>
              <p>
                Our team works directly with manufacturers and mills across the globe to source 
                materials that meet our exacting standards. Every fabric in our collection 
                represents our commitment to quality and value.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-4 block">
              Our Values
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#1C1C1A]">
              What We Stand For
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            {values.map((value) => (
              <div 
                key={value.title} 
                className="text-center"
                data-testid={`value-${value.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border border-[#E6E4DD]">
                  <value.icon size={28} className="text-[#4A5D4E]" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1C1C1A] mb-3">
                  {value.title}
                </h3>
                <p className="text-[#6B6A65] font-light text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 md:py-32 bg-[#1C1C1A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-3 gap-8 md:gap-12 text-center">
            <div data-testid="stat-products">
              <p className="font-['Cormorant_Garamond'] text-5xl md:text-6xl font-light text-[#F7F5F0] mb-2">
                5+
              </p>
              <p className="text-[#F7F5F0]/60 text-sm uppercase tracking-wide">
                Fabric Collections
              </p>
            </div>
            <div data-testid="stat-clients">
              <p className="font-['Cormorant_Garamond'] text-5xl md:text-6xl font-light text-[#F7F5F0] mb-2">
                200+
              </p>
              <p className="text-[#F7F5F0]/60 text-sm uppercase tracking-wide">
                Happy Clients
              </p>
            </div>
            <div data-testid="stat-experience">
              <p className="font-['Cormorant_Garamond'] text-5xl md:text-6xl font-light text-[#F7F5F0] mb-2">
                4+
              </p>
              <p className="text-[#F7F5F0]/60 text-sm uppercase tracking-wide">
                Years Experience
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#1C1C1A] mb-6">
            Partner With Us
          </h2>
          <p className="text-[#6B6A65] font-light leading-relaxed mb-10 max-w-2xl mx-auto">
            Whether you&apos;re looking for specific fabric types or want to explore our collections, 
            we&apos;re here to help. Let&apos;s discuss how we can meet your fabric needs.
          </p>
          <Link
            to="/contact"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="partner-cta-btn"
          >
            Get in Touch
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
